import { Sparkles, Wrench } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import useWhatsNew from "../hooks/useWhatsNew";
import { formatReleaseDate, isMajorUpdate, whatsNewTitle } from "../lib/whatsNew";

function Section({ icon, title, items, testid }) {
  if (items.length === 0) return null;
  return (
    <section data-testid={testid} className="space-y-1.5">
      <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {icon}
        {title}
      </h4>
      <ul className="space-y-1.5 text-sm">
        {items.map((item) => (
          <li key={item} className="flex gap-2">
            <span aria-hidden="true" className="mt-2 h-1 w-1 shrink-0 rounded-full bg-jamora" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function VersionBlock({ entry, withHeading }) {
  const date = formatReleaseDate(entry.date);
  return (
    <div className="space-y-3" data-testid={`whats-new-version-${entry.version}`}>
      {withHeading && (
        <h3 className="text-sm font-semibold text-jamora">
          Version {entry.version}
          {date && <span className="ml-2 text-xs font-normal text-muted-foreground">{date}</span>}
        </h3>
      )}
      <Section icon={<Sparkles size={14} aria-hidden="true" />} title="Nouveautés" items={entry.features} testid="whats-new-features" />
      <Section icon={<Wrench size={14} aria-hidden="true" />} title="Corrections" items={entry.fixes} testid="whats-new-fixes" />
    </div>
  );
}

// « Nouveautés » panel (lot S5 of the versions). Opens once on the first sign-in after a minor or major version (never for a plain fix) and
// reopens from the version number at the foot of the sidebar. Never blocks work: closable everywhere (Esc, click outside, button); a bottom
// sheet on a phone (`ui/dialog.jsx`).
export default function WhatsNewDialog() {
  const { open, versions, close } = useWhatsNew();
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="whats-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={16} className="text-jamora" aria-hidden="true" />
            {whatsNewTitle(versions)}
          </DialogTitle>
          <DialogDescription>
            {isMajorUpdate(versions) ? "Cette version change des habitudes : voici l'essentiel." : "Voici ce qui change depuis votre dernière visite."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {versions.map((entry) => <VersionBlock key={entry.version} entry={entry} withHeading={versions.length > 1} />)}
        </div>
        <DialogFooter>
          <Button onClick={close} data-testid="whats-new-close">J'ai compris</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
